import React, { useEffect } from 'react';
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInstagram } from "@fortawesome/free-brands-svg-icons";
import { faScissors, faPaintBrush, faGem, faUserGraduate, faHeart } from "@fortawesome/free-solid-svg-icons";
import Footer from "../components/common/footer";
import SEOHelmet from "../components/common/SEOHelmet";
import { generatePersonSchema, generateWebPageSchema } from "../utils/schemaGenerators";
import INFO from "../data/user";
import SEO from "../data/seo";

import "./styles/about.css";

const About = () => {
    const currentSEO = SEO.find(item => item.page === "about");
    
    const pageSchema = [
        generatePersonSchema(),
        generateWebPageSchema({
            title: "About Anna - Master Hair Stylist",
            description: currentSEO?.description,
            url: `${INFO.socials.website}/about`
        })
    ];

    useEffect(() => {
        // Scroll to top on component mount
        window.scrollTo(0, 0);
        
        // Add animation for elements when they come into view
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate');
                }
            });
        }, { threshold: 0.1 });
        
        // Observe all elements with fade-in class
        document.querySelectorAll('.fade-in').forEach(el => {
            observer.observe(el);
        });
        
        return () => {
            // Clean up observer on component unmount
            observer.disconnect();
        };
    }, []);

    const specialties = [
        { 
            name: "Balayage", 
            icon: faPaintBrush, 
            description: "Natural-looking, sun-kissed highlights that grow out beautifully" 
        },
        { 
            name: "Precision Cuts", 
            icon: faScissors, 
            description: "Customized haircuts tailored to your face shape and lifestyle" 
        },
        { 
            name: "Color Correction", 
            icon: faGem, 
            description: "Transforming color mishaps into beautiful, healthy hair" 
        }
    ];

    return (
        <React.Fragment>
            <SEOHelmet
                title="About Anna - Master Hair Stylist & Balayage Specialist"
                description="Meet Anna, award-winning hair stylist in Falls Church, VA. 5+ years experience in balayage, color correction & precision cuts. Specializing in Asian hair. Book your transformation!"
                keywords={currentSEO?.keywords.join(", ")}
                schema={pageSchema}
            />

            <div className="page-content">
                <div className="content-wrapper">
                    {/* Hero Section */}
                    <div className="about-hero">
                        <div className="about-hero-content">
                            <h1 className="about-hero-title fade-in">Meet Anna</h1>
                            <div className="about-hero-subtitle fade-in">Professional Hair Stylist & Color Specialist</div>
                            <div className="about-hero-quote fade-in">
                                "Creating beauty is not just my profession, it's my passion"
                            </div>
                        </div>
                    </div>

                    <div className="about-container">
                        {/* Main Info Section */}
                        <div className="about-main">
                            <div className="about-left-side fade-in">
                                <div className="about-section-title">
                                    <span className="title-highlight">About</span> Me
                                </div>
                                <div className="about-description">
                                    <p>
                                        Welcome! I'm Anna, a passionate hair stylist from Ukraine with over 5 years of experience in creating beautiful, personalized looks for my clients. My journey in the beauty industry has been driven by my love for transforming not just hair, but helping people feel confident and beautiful.
                                    </p>
                                    <p>
                                        I specialize in balayage, color correction, and precision haircuts. My approach combines technical expertise with an artistic eye to create looks that enhance your natural beauty while being easy to maintain.
                                    </p>
                                    <p>
                                        Whether you're looking for a subtle change or a complete transformation, I'm here to listen to your needs and create a look that makes you feel amazing. I believe that great hair should be accessible to everyone, and I take pride in educating my clients about hair care and maintenance.
                                    </p>
                                </div>
                                <div className="about-cta">
                                    <a 
                                        href={INFO.socials.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="about-cta-btn"
                                    >
                                        <FontAwesomeIcon icon={faInstagram} /> Follow My Work
                                    </a>
                                    {/* <a 
                                        href={INFO.socials.vagaro || "#booking"}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="about-cta-btn book-btn"
                                    >
                                        Book an Appointment
                                    </a> */}
                                </div>
                            </div>
                            <div className="about-right-side fade-in">
                                <div className="about-image-container">
                                <img 
                                    src="/homepage.avif" 
                                    alt="Anna Veretynska - Master Hair Stylist and Balayage Specialist in Falls Church, VA"
                                    className="about-image"
                                    loading="lazy"
                                    width="400"
                                    height="500"
                                />
                                    <div className="image-overlay"></div>
                                    <div className="experience-badge">
                                        <span className="years">5+</span>
                                        <span className="text">Years Experience</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Specialties Section */}
                        <div className="about-specialties fade-in">
                            <div className="about-section-title centered">
                                My <span className="title-highlight">Specialties</span>
                            </div>
                            <div className="specialties-container">
                                {specialties.map((specialty, index) => (
                                    <div className="specialty-card" key={index}>
                                        <div className="specialty-icon">
                                            <FontAwesomeIcon icon={specialty.icon} />
                                        </div>
                                        <h3 className="specialty-title">{specialty.name}</h3>
                                        <p className="specialty-description">{specialty.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Philosophy Section */}
                        <div className="about-philosophy fade-in">
                            <div className="philosophy-content">
                                <div className="about-section-title">
                                    My <span className="title-highlight">Philosophy</span>
                                </div>
                                <p className="philosophy-text">
                                    I believe that every client deserves personalized attention and a customized approach to their hair. Beauty isn't about following trends blindly—it's about enhancing your unique features and helping you feel confident in your own skin.
                                </p>
                                <div className="philosophy-points">
                                    <div className="philosophy-point">
                                        <FontAwesomeIcon icon={faUserGraduate} className="point-icon" />
                                        <div className="point-content">
                                            <h4>Education First</h4>
                                            <p>I'm committed to ongoing education to bring you the latest techniques and trends.</p>
                                        </div>
                                    </div>
                                    <div className="philosophy-point">
                                        <FontAwesomeIcon icon={faHeart} className="point-icon" />
                                        <div className="point-content">
                                            <h4>Client Happiness</h4>
                                            <p>Your satisfaction is my priority. I take the time to understand your needs and expectations.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* <div className="philosophy-image-container">
                                <img 
                                    src="/professional.jpg" 
                                    alt="Anna working on client's hair"
                                    className="philosophy-image"
                                    onError={(e) => {e.target.src = "/homepage.avif"; e.target.style.opacity = "0.7";}}
                                />
                            </div> */}
                        </div>

                        {/* Testimonial Section */}
                        <div className="about-testimonial fade-in">
                            <div className="testimonial-content">
                                <div className="quote-mark">"</div>
                                <p className="testimonial-text">
                                    Anna transformed my hair completely! She listened to what I wanted and delivered beyond my expectations. Her attention to detail and knowledge about hair care is impressive.
                                </p>
                                <div className="testimonial-author">
                                    <div className="author-name">Sarah Johnson</div>
                                    <div className="author-title">Loyal Client</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="page-footer">
                        <Footer />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default About; 